import type { BaseSyntheticEvent } from 'react';
import type { FieldValues, FormState, UseFormHandleSubmit } from 'react-hook-form';
import type { PatternCompositeFilterDescriptor, PatternFilterDescriptor } from '@ricoh/ria.client.core/types/component/pattern.type';

import { isNotNull } from '@ricoh/ria.client.core/utils/data/object';

/**
 * Type guard for `PatternCompositeFilterDescriptor`.
 *
 * @param {PatternCompositeFilterDescriptor | PatternFilterDescriptor} source Source.
 *
 * @returns {boolean} Source is `PatternCompositeFilterDescriptor`
 */
export function isPatternCompositeFilterDescriptor(
  source: PatternCompositeFilterDescriptor | PatternFilterDescriptor,
): source is PatternCompositeFilterDescriptor {
  return 'filters' in source && isNotNull(source.filters);
}

/**
 * Check form is dirty.
 *
 * @template TFieldValues Field values.
 * @param {FormState<TFieldValues>} formState Form state.
 *
 * @returns {boolean} Form is dirty.
 */
export function isFormDirty<TFieldValues extends FieldValues>(formState: FormState<TFieldValues>): boolean {
  return formState.isDirty || Object.keys(formState.dirtyFields).length > 0;
}

/**
 * Submit Form.
 *
 * @template TFieldValues Field values.
 * @template THandleSubmit Handle submit.
 * @param {UseFormHandleSubmit<TFieldValues>} handleSubmit Handle Submit.
 * @param {BaseSyntheticEvent} event Event.
 *
 * @returns {TFieldValues} SubmitData.
 */
export async function submitForm<TFieldValues extends FieldValues, THandleSubmit extends UseFormHandleSubmit<TFieldValues> = UseFormHandleSubmit<TFieldValues>>(
  handleSubmit: THandleSubmit,
  event?: BaseSyntheticEvent,
): Promise<TFieldValues> {
  return new Promise<TFieldValues>((resolve, reject) => {
    const submit = handleSubmit(resolve, reject);
    submit(event).catch(reject);
  });
}
